import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import CustomSelect from '../../components/forms/CustomSelect';
import RequiredInput from '../../components/forms/InputHOC';
import useOrderTypes from "../../hooks/api-calls/orders/useOrderTypes";
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { menuRoutes } from '../../router/menu';
import { Order } from "../../type/entities/order-type";
import { getLastDayOfNextMonth, subtractSevenDays } from '../../utils/getFormattedDate';
import FormFooter from '../_layout/_footers/form-footer';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';

interface OrderFormProps {
    isLoading: boolean;
    submit: Function;
    data?: Order | undefined;
}

const schema = yup.object({
    orderTypeId: yup.string().required('El tipo de pedido es obligatorio'),
    code: yup.string().notRequired().nullable(),
    startDate: yup.string().required('La fecha de inicio es obligatoria'),
    endDate: yup.string().required('La fecha de fin es obligatoria'),
    collectionDate: yup.string().nullable(),
    orderDate: yup.string().nullable(),
});

const OrderForm: React.FC<OrderFormProps> = ({ isLoading, submit, data }) => {

    const mode = data ? 'Editar' : 'Crear';
    const navigate = useNavigate();
    const { getOrderTypesList } = useOrderTypes();
    const { userCan } = usePrivilege();

    const formik = useFormik({
        initialValues: {
            orderId: data?.id || '',
            orderTypeId: data?.orderTypes?.id || '',
            code: data?.code || '',
            startDate: data?.startDate?.date.split(' ')[0] ?? '',
            endDate: data?.endDate?.date.split(' ')[0] ?? '',
            collectionDate: data?.collectionDate?.date.split(' ')[0] ?? '',
            orderDate: data?.orderDate?.date.split(' ')[0] ?? '',
        },
        validationSchema: schema,
        onSubmit: values => { submit(values) },
    });

    /**
     * Set orderDate and collectionDate when startDate and endDate are set
     */
    useEffect(() => {
        // Set orderDate 7 days before startDate
        if (formik.values.startDate && !formik.values.orderDate) {
            formik.setFieldValue('orderDate', subtractSevenDays(new Date(formik.values.startDate)));
        }

        // Set collectionDate to the last day of the next month of endDate
        if (formik.values.endDate && !formik.values.collectionDate) {
            formik.setFieldValue('collectionDate', getLastDayOfNextMonth(new Date(formik.values.endDate)));
        }
    }, [formik.values.startDate, formik.values.endDate]);

    const getContent = () => {
        return (
            <div className="@container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 font-medium">
                        <FormGroup
                            title="Información General"
                            description="Datos principales"
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-12"
                        >
                            <div className="md:col-span-8 space-y-2">
                                <CustomSelect
                                    isSearchable
                                    isClearable
                                    id={'orderTypeId'}
                                    label="Tipo de Pedido"
                                    value={getOrderTypesList()?.find((g: any) => g.value === formik.values.orderTypeId)}
                                    options={getOrderTypesList()}
                                    onChange={(e: any) => { formik.setFieldValue('orderTypeId', e?.value) }}
                                    required
                                    error={formik.errors.orderTypeId}
                                    isDisabled={!userCan('get_order_types_for_select', 'order_types')}
                                />
                                {!userCan('get_order_types_for_select', 'order_types') && (
                                    <div className="flex items-center gap-2 text-red-500 text-sm">
                                        <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                        <span>No tienes permisos para ver los tipos de pedido</span>
                                    </div>
                                )}
                            </div>
                            <RequiredInput
                                id='code'
                                required={false}
                                className="md:col-span-4"
                                label={<>Código de Pedido &nbsp;<HiOutlineInformationCircle size={20} color={'primary'} /></>}
                                contentTooltipText="Si no se ingresa un código, se generará automáticamente"
                                formik={formik}
                            />

                            <RequiredInput
                                id='startDate'
                                type="date"
                                className="md:col-span-3"
                                label="Fecha de Inicio"
                                formik={formik}
                            />

                            <RequiredInput
                                id='endDate'
                                type="date"
                                className="md:col-span-3"
                                label="Fecha de Fin"
                                formik={formik}
                            />

                            <RequiredInput
                                id='collectionDate'
                                required={false}
                                type="date"
                                className="md:col-span-3"
                                label="Fecha de Cobro"
                                formik={formik}
                            />

                            <RequiredInput
                                id='orderDate'
                                required={false}
                                type="date"
                                className="md:col-span-3"
                                label="Fecha de Pedido"
                                formik={formik}
                            />
                        </FormGroup>
                    </div>
                </form>
            </div>
        )
    };

    return (
        <>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>
            <FormFooter
                submitBtnText={mode + ' Pedido'}
                handleCancelBtn={() => navigate(menuRoutes.orders.path + menuRoutes.orders.list as string)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </>
    );
};

export default OrderForm;